import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Link as LinkIcon, X, HelpCircle } from "lucide-react";
import { Button } from "../ui/button";
import type { NearMissSlot } from "../../lib/insights/blockers";
import type { Slot, VoteType } from "../../lib/insights/types";
import { formatDate, formatTime } from "../voting/utils/dateUtils";

export interface BlockerCalloutProps {
  nearMiss: NearMissSlot;
  slot: Slot;
  /** Has the organizer already copied the link for this participant? */
  isShared: (participantId: string) => boolean;
  /** Click handler — copies the poll URL and marks the participant as shared */
  onCopyLink: (participantId: string) => void;
}

const VoteBadge: React.FC<{ vote: VoteType | null }> = ({ vote }) => {
  const { t } = useTranslation("polls");
  if (vote === "no")
    return (
      <span
        className="inline-flex items-center gap-0.5 text-vote-unavailable-foreground"
        aria-label={t("blockers.vote.no")}
      >
        <X className="w-3 h-3" />
      </span>
    );
  if (vote === "maybe")
    return (
      <span
        className="inline-flex items-center gap-0.5 text-vote-maybe-foreground"
        aria-label={t("blockers.vote.maybe")}
      >
        <HelpCircle className="w-3 h-3" />
      </span>
    );
  return (
    <span
      className="text-muted-foreground/70 text-xs"
      aria-label={t("blockers.vote.none")}
    >
      —
    </span>
  );
};

function slotLabel(s: Slot): string {
  const date = formatDate(s.primaryDate);
  if (s.timeSlots && s.timeSlots.length > 0) {
    return `${date} · ${formatTime(s.timeSlots)}`;
  }
  return date;
}

const BlockerCallout: React.FC<BlockerCalloutProps> = ({
  nearMiss,
  slot,
  isShared,
  onCopyLink,
}) => {
  const { t } = useTranslation("polls");
  const gapLabel = t("blockers.gap", { count: nearMiss.gap });
  return (
    <article
      className="rounded-2xl border border-warning/40 bg-warning/[0.06] p-3 space-y-2"
      data-testid={`blocker-callout-${nearMiss.slotId}`}
      aria-labelledby={`blocker-heading-${nearMiss.slotId}`}
    >
      <header className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-warning" aria-hidden />
        <h4
          id={`blocker-heading-${nearMiss.slotId}`}
          className="text-sm font-medium text-foreground"
        >
          {slotLabel(slot)}
        </h4>
        <span
          className="text-xs text-warning ml-auto"
          data-testid={`blocker-gap-${nearMiss.slotId}`}
        >
          −{gapLabel}
        </span>
      </header>
      <ul className="divide-y divide-warning/20">
        {nearMiss.blockers.map((b) => {
          const shared = isShared(b.participantId);
          return (
            <li
              key={b.participantId}
              data-testid={`blocker-row-${nearMiss.slotId}-${b.participantId}`}
              className="flex items-center justify-between gap-2 py-1.5"
            >
              <span className="flex items-center gap-2 text-sm text-foreground truncate">
                <VoteBadge vote={b.vote} />
                <span className="truncate" title={b.name}>
                  {b.name}
                </span>
              </span>
              <Button
                type="button"
                variant={shared ? "outline" : "ghost"}
                size="sm"
                onClick={() => onCopyLink(b.participantId)}
                data-testid={`blocker-copy-${nearMiss.slotId}-${b.participantId}`}
                data-state={shared ? "shared" : "idle"}
                className="gap-1"
              >
                <LinkIcon className="w-3.5 h-3.5" aria-hidden />
                {shared ? t("blockers.copyAgain") : t("blockers.copyLink")}
              </Button>
            </li>
          );
        })}
      </ul>
    </article>
  );
};

export default BlockerCallout;
